import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { api } from './api.js';
import { useToast } from '../components/Toast.js';
import type { ChecklistItemType, ChecklistToggleResponse } from './contracts.js';

export type ChecklistToggleVars = {
  itemType: ChecklistItemType;
  itemId: string;
  done: boolean;
};

/**
 * The learner-owned done/not-done tick — the only thing an assignment or a
 * capstone milestone persists anywhere in this product.
 *
 * A checkbox that lags a round trip feels broken, so the write is optimistic:
 * `patch` rewrites the one cached payload the tick belongs to, and a failure
 * puts the old payload back and says so out loud rather than leaving a tick
 * that never reached the server. Only `queryKey` is invalidated — a milestone
 * tick has no business refetching the dashboard.
 */
export function useChecklistToggle<TData>(
  queryKey: readonly unknown[],
  patch: (previous: TData, itemId: string, done: boolean) => TData,
): UseMutationResult<
  ChecklistToggleResponse,
  unknown,
  ChecklistToggleVars,
  { previous: TData | undefined }
> {
  const queryClient = useQueryClient();
  const { toastError } = useToast();

  return useMutation({
    mutationFn: (vars: ChecklistToggleVars) =>
      api.post<ChecklistToggleResponse>('/progress/checklist', vars),

    onMutate: async (vars) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<TData>(queryKey);
      if (previous !== undefined) {
        queryClient.setQueryData<TData>(queryKey, patch(previous, vars.itemId, vars.done));
      }
      return { previous };
    },

    onError: (error, _vars, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData<TData>(queryKey, context.previous);
      }
      toastError(error, 'That tick could not be saved — it has been put back.');
    },

    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey });
    },
  });
}
